import { useEffect, useRef, type ReactNode } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { describedBy, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Checkbox with its text to the right (the text is the accessible name), plus
 * helper and error text below. The text is beside the box whatever the label
 * placement setting is: `labelPlacement` does not apply to checkboxes. A null value (not undefined) shows the indeterminate state, as
 * DevExtreme's CheckBox did.
 *
 * onValueChanged fires only when a person ticks or unticks it, never when
 * `value` changes. Read-only and disabled boxes do not change.
 */
export interface CheckboxFieldProps extends Pick<BaseFieldProps,
  'ariaLabel' | 'disabled' | 'readOnly' | 'error' | 'isValid' | 'errorText' | 'helperText' | 'className' | 'inputAttr'> {
  /** The text beside the box, and its accessible name. May carry a
   *  decorative icon (aria-hidden) ahead of the words. */
  label?: ReactNode;
  value: boolean | null | undefined;
  /** The new state. User changes only. */
  onValueChanged: (value: boolean) => void;
}

export default function CheckboxField(props: CheckboxFieldProps) {
  const { value, onValueChanged, label, ariaLabel, disabled, readOnly, helperText, errorText, className, inputAttr } = props;
  const ids = useFieldIds(inputAttr);
  const invalid = isInvalid(props);
  // MUI draws the dash but leaves the input's own `indeterminate` off; set it
  // after every render (a click clears it) so assistive technology reads the
  // box as mixed, as it did a native one.
  const inputEl = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputEl.current) inputEl.current.indeterminate = value === null;
  });
  const box = (
    <Checkbox
      id={ids.id}
      size="small"
      checked={value === true}
      indeterminate={value === null}
      inputRef={inputEl}
      disabled={disabled}
      onChange={(e) => { if (!readOnly) onValueChanged(e.target.checked); }}
      inputProps={{
        'aria-label': label ? undefined : ariaLabel,
        'aria-invalid': invalid || undefined,
        'aria-readonly': readOnly || undefined,
        'aria-describedby': describedBy(ids, { helperText, errorText }),
      } as Record<string, unknown>}
    />
  );
  return (
    <div className={['app-check', invalid && 'app-check--invalid', className].filter(Boolean).join(' ')}>
      {label ? <FormControlLabel control={box} label={label} disabled={disabled} /> : box}
      {errorText && <div id={ids.errorId} className="app-field-error">{errorText}</div>}
      {helperText && <div id={ids.helperId} className="app-field-helper">{helperText}</div>}
    </div>
  );
}
